import {
    ActionButton,
    MessageBarType,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
} from '@fluentui/react';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ApiException,
    Checkbox,
    Label,
    MessageBar,
    TextField,
} from '@eta/components';
import { NavLink } from 'react-router-dom';
import { isArray } from 'util';
import { MarkDownType } from '../common/config/constants';
import PrivacyPolicy from '../AcceptInvitation/components/PrivacyPolicy';
import { WizardStateAndLogic } from '../common/component/WizardPivot';
import { DocumentSigning } from '../documentSigning/DocumentSigning';
import { itemsKey } from './TaxpayerSelfRegistryMain';
import { ServiceCreator } from './Service/client';
import {
    NewSessionResponse,
    VerifySessionRequestDto,
} from './Service/TaxpayerProxy';
import './TaxpayerSignature.scss';

export interface TaxpayerSignatureProps {
    setFooter: (footer: JSX.Element) => void;
    taxpayerSelfRegistryWizard: WizardStateAndLogic;
    resetWizard?: (resetWizard: boolean) => void;
    setRin: (rin?: string) => void;
    setNewSession: (session: NewSessionResponse) => void;
}

export const TaxpayerSignature: FC<TaxpayerSignatureProps> = (
    props: TaxpayerSignatureProps
) => {
    const { t } = useTranslation(['SignUp']);
    const {
        setFooter,
        taxpayerSelfRegistryWizard,
        setRin,
        resetWizard,
        setNewSession,
    } = props;
    const [privacyType, setPrivacyType] = useState<string>();
    const [rinValue, setRinValue] = useState<string>();
    const [privacyChecked, setPrivacyChecked] =
        useState<boolean | undefined>(false);
    const [hasErrorr, setHasErrorr] = useState<boolean>(false);
    const [errorrMsg, setErrorrMsg] = useState<string | JSX.Element>();
    const [canGoNext, setCanGoNext] = useState<boolean>(false);
    const [SigningContent, setSigningContent] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const client = ServiceCreator.getTaxpayerManagementPublicClient(true);
    const sessionID = useRef<string>();
  
    const startSigning = () => {
        setSigningContent([
            JSON.stringify(
                { data: t('SigningContent', {
                    rin: rinValue,
                    number: sessionID.current,
                }) }
            ),
        ]);
        setIsLoading(false);
    };

    const signTaxpayer = async (signResult: string) => {
        try {
            setIsLoading(true);
            const parsedResult = JSON.parse(signResult);
            const signture = isArray(parsedResult)
                ? parsedResult[0].signature
                : parsedResult.signature;

            const result = await client.verifySession(
                sessionID.current ?? '',
                new VerifySessionRequestDto({ signature: signture, document: SigningContent[0] })
            );
            
            if (result.errors?.length) {
                setHasErrorr(true);
                setErrorrMsg(t('SignatureNotVerify'));
                return;
            } if (resetWizard) resetWizard(result.hasDigitalProfile);
            taxpayerSelfRegistryWizard.ChangeTabState(
                itemsKey.Signature,
                'completed'
            );
            taxpayerSelfRegistryWizard.nextTab();
        } catch {
            setIsLoading(false);
            setHasErrorr(true);
            setErrorrMsg(t('SignatureNotVerify'));
        } finally {
            setIsLoading(false);
        }
    };

    const startNewSession = async () => {
        setIsLoading(true);
        try {
            const response = await client.startNewSession(rinValue ?? '');
            sessionID.current = response.sessionId;
            setNewSession(response);
            setRin(rinValue);
            startSigning();
        } catch (ex) {
            setIsLoading(false);
            const exception = ex as ApiException;
            setHasErrorr(true);
            setErrorrMsg(
                exception?.serviceError ? (
                    <span>
                        {`${t('NumberNotFound')} `}
                        <NavLink exact to="" target="_blank">
                            {t('ContactETA')}
                        </NavLink>
                        {` ${t('ForDetails')}`}
                    </span>
                ) : (
                    exception.toString()
                )
            );
            taxpayerSelfRegistryWizard.ChangeTabState(
                itemsKey.Signature,
                'error'
            );
        }
    };

    useEffect(() => {
        setFooter(
           <PrimaryButton
                className="continue"
                menuIconProps={{ iconName: 'Forward' }}
                disabled={!canGoNext || isLoading}
                text={t('Sign')}
                onClick={() => startNewSession()}
            >
                {isLoading && <Spinner size={SpinnerSize.small} />}
            </PrimaryButton>
           );
    }, [canGoNext, t, isLoading, rinValue]);

    useEffect(() => {
        setCanGoNext(!!rinValue && (privacyChecked ?? false));
    }, [rinValue, privacyChecked]);

    return (
        <div className="SignUp_TaxpayerSignature">
            {hasErrorr && (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    isMultiline={false}
                    className="messageBar"
                    onDismiss={() => {
                        setHasErrorr(false);
                    }}
                >
                    {t('PleaseCheckErrors')}
                </MessageBar>
            )}
            <div className="bodyLine-TaxpayerSignature">
                <div className="leftPartbody">
                    <Label>{t('TaxpayerSignatureDesc')}</Label>
                    <div className="rightPart">
                        <img
                            className="noDraftTempalteImage"
                            src="/coashMarkInfo.svg"
                            alt="test"
                        />
                    </div>
                </div>
                <Separator vertical />
                <div className="rightPartbody">
                    <Label className="fill-label">
                        {t('FillRequiredFields')}
                    </Label>
                    <div className="registrationField">
                        <TextField
                            className="RegistrationNumber"
                            label={t('RegistrationNumber')}
                            maxLength={50}
                            required
                            value={rinValue}
                            onChange={(_, v) => {
                                setErrorrMsg('');
                                setRinValue(v);
                            }}
                        />
                        <span className="errorrMsg">{errorrMsg}</span>
                    </div>
                    <div className="privacy">
                        <Checkbox
                            checked={privacyChecked}
                            onChange={(_, checked) => {
                                setPrivacyChecked(checked);
                            }}
                        />
                        <span className="PrivacyPolicy">
                            <span className="grayColor">
                                {t('AcceptPrivacy')}
                            </span>
                            <ActionButton
                                onClick={() =>
                                    setPrivacyType(MarkDownType.Terms)
                                }
                            >
                                {t('TermsAndCondtion')}
                            </ActionButton>
                            <span className="grayColor">{t('And')}</span>
                            <ActionButton
                                onClick={() =>
                                    setPrivacyType(
                                        MarkDownType.RegistrationPrivacy
                                    )
                                }
                            >
                                {t('PrivacyPolicy')}
                            </ActionButton>
                        </span>
                    </div>
                </div>
            </div>
            {privacyType && (
                <PrivacyPolicy
                    onClose={() => setPrivacyType(undefined)}
                    type={privacyType}
                    hidden={!privacyType}
                />
            )}
            <DocumentSigning
                hidden
                content={SigningContent}
                onSign={(response: string) => {
                    signTaxpayer(response);
                }}
                isLoading={false}
            />
        </div>
    );
};
